/*
 * Klaviyo Back in Stock
 * ---------------------
 * Registers a Back in Stock subscription against Klaviyo's public Client API
 * straight from the browser — no popup, no third-party app, no backend.
 *
 * Markup is rendered by snippets/back-in-stock.liquid. The theme re-renders
 * that snippet on every variant change, so each <klaviyo-back-in-stock>
 * instance always carries the id of the currently selected, sold-out variant.
 *
 * Docs: https://developers.klaviyo.com/en/reference/create_client_back_in_stock_subscription
 */
(function () {
  'use strict';

  if (customElements.get('klaviyo-back-in-stock')) return;

  var ENDPOINT = 'https://a.klaviyo.com/client/back-in-stock-subscriptions/';
  var EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  var ICONS = {
    success:
      '<svg class="icon icon-sm shrink-0" viewBox="0 0 24 24" stroke="currentColor" fill="none" role="presentation"><path stroke-linecap="round" stroke-linejoin="round" d="M9 12L11 14L16 9M22 12C22 17.5228 17.5228 22 12 22C6.47715 22 2 17.5228 2 12C2 6.47715 6.47715 2 12 2C17.5228 2 22 6.47715 22 12Z"></path></svg>',
    error:
      '<svg class="icon icon-sm shrink-0" viewBox="0 0 24 24" stroke="currentColor" fill="none" role="presentation"><path stroke-linecap="round" stroke-linejoin="round" d="M12 13V9M12.5 16.5C12.5 16.7761 12.2761 17 12 17C11.7239 17 11.5 16.7761 11.5 16.5M12.5 16.5C12.5 16.2239 12.2761 16 12 16C11.7239 16 11.5 16.2239 11.5 16.5M12.5 16.5H11.5M19.3311 10.0912L18.98 9.46437C16.6988 5.39063 15.5581 3.35377 14.0576 2.67625C12.7495 2.08558 11.2505 2.08558 9.94239 2.67625C8.44189 3.35377 7.30124 5.39064 5.01995 9.46438L4.66894 10.0912C2.47606 14.007 1.37961 15.965 1.56302 17.5683C1.72303 18.967 2.46536 20.2335 3.60763 21.0566C4.91688 22 7.16092 22 11.649 22H12.351C16.8391 22 19.0831 22 20.3924 21.0566C21.5346 20.2335 22.277 18.967 22.437 17.5683C22.6204 15.965 21.5239 14.007 19.3311 10.0912Z"></path></svg>'
  };

  var DEFAULT_STRINGS = {
    success: 'Perfecto, te avisaremos por email cuando vuelva a estar disponible.',
    error: 'No hemos podido registrar tu solicitud. Inténtalo de nuevo en unos minutos.',
    invalid: 'Introduce una dirección de email válida.',
    already: 'Ya tienes activado el aviso para este producto. Te escribiremos cuando vuelva.',
    sending: 'Enviando…'
  };

  function storageKey(variantId, email) {
    return 'klaviyo-bis:' + variantId + ':' + email.toLowerCase();
  }

  function readFlag(key) {
    try {
      return window.localStorage.getItem(key) === '1';
    } catch (e) {
      return false;
    }
  }

  function writeFlag(key) {
    try {
      window.localStorage.setItem(key, '1');
    } catch (e) {
      /* private mode / storage disabled — ignore */
    }
  }

  class KlaviyoBackInStock extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;

      this.companyId = this.dataset.companyId;
      this.revision = this.dataset.revision || '2026-07-15';
      this.variantId = this.dataset.variantId;

      this.form = this.querySelector('form');
      this.emailInput = this.querySelector('[data-bis-email]');
      this.buttons = this.querySelectorAll('[data-bis-submit]');
      this.feedback = this.querySelector('[data-bis-feedback]');
      this.strings = this.readStrings();

      if (!this.form || !this.emailInput) return;

      this.onSubmit = this.onSubmit.bind(this);
      this.form.addEventListener('submit', this.onSubmit);
    }

    disconnectedCallback() {
      if (this.form) this.form.removeEventListener('submit', this.onSubmit);
    }

    readStrings() {
      var node = this.querySelector('[data-bis-strings]');
      var parsed = {};
      if (node) {
        try {
          parsed = JSON.parse(node.textContent);
        } catch (e) {
          parsed = {};
        }
      }
      return Object.assign({}, DEFAULT_STRINGS, parsed);
    }

    onSubmit(event) {
      event.preventDefault();
      if (this.sending) return;

      var email = (this.emailInput.value || '').trim();

      if (!EMAIL_RE.test(email)) {
        this.showFeedback('error', this.strings.invalid);
        this.emailInput.classList.add('invalid');
        this.emailInput.focus();
        return;
      }
      this.emailInput.classList.remove('invalid');

      if (!this.companyId || this.companyId === 'PEGA_AQUI_TU_PUBLIC_KEY') {
        console.warn('[Klaviyo BIS] Falta configurar la Public API Key en snippets/back-in-stock.liquid.');
        this.showFeedback('error', this.strings.error);
        return;
      }

      if (readFlag(storageKey(this.variantId, email))) {
        this.showFeedback('success', this.strings.already);
        return;
      }

      this.subscribe(email);
    }

    subscribe(email) {
      var self = this;
      this.setSending(true);
      this.showFeedback('info', this.strings.sending);

      var body = {
        data: {
          type: 'back-in-stock-subscription',
          attributes: {
            channels: ['EMAIL'],
            profile: {
              data: {
                type: 'profile',
                attributes: { email: email }
              }
            }
          },
          relationships: {
            variant: {
              data: {
                type: 'catalog-variant',
                id: '$shopify:::$default:::' + this.variantId
              }
            }
          }
        }
      };

      fetch(ENDPOINT + '?company_id=' + encodeURIComponent(this.companyId), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          revision: this.revision
        },
        body: JSON.stringify(body)
      })
        .then(function (response) {
          if (response.status === 202 || response.ok) {
            writeFlag(storageKey(self.variantId, email));
            self.showFeedback('success', self.strings.success);
            if (self.form) self.form.hidden = true;
          } else {
            self.setSending(false);
            self.showFeedback('error', self.strings.error);
            response.text().then(function (text) {
              console.error('[Klaviyo BIS] HTTP ' + response.status + ': ' + text);
            });
          }
        })
        .catch(function (error) {
          self.setSending(false);
          self.showFeedback('error', self.strings.error);
          console.error('[Klaviyo BIS] Network error', error);
        });
    }

    setSending(state) {
      this.sending = state;
      this.buttons.forEach(function (button) {
        button.disabled = state;
        button.setAttribute('aria-busy', state ? 'true' : 'false');
      });
    }

    showFeedback(status, message) {
      if (!this.feedback) return;
      var icon = ICONS[status] || '';
      this.feedback.innerHTML =
        '<div class="alert alert--' +
        status +
        ' flex items-start gap-3 text-sm md:text-base leading-tight" role="status">' +
        icon +
        '<span>' +
        message +
        '</span></div>';
      this.feedback.hidden = false;
    }
  }

  customElements.define('klaviyo-back-in-stock', KlaviyoBackInStock);
})();
